import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import { ProgressBar } from 'primereact/progressbar';
import axios from 'axios';
import { Dropdown } from 'primereact/dropdown';
import moment from 'moment';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { InputSwitch } from 'primereact/inputswitch';
import { Button } from 'primereact/button';
import { Badge } from 'primereact/badge';
import { InputText } from 'primereact/inputtext';
import { Sidebar } from 'primereact/sidebar';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import huLocale from '@fullcalendar/core/locales/hu';
import deLocale from '@fullcalendar/core/locales/de';

import {droppedProductionItem} from '../scheduling/assignmentApi';

import ScheduleitemListComponent from './ScheduleitemListComponent';

const ScheduleitemCalendarComponent = (props) => {
  const { t, scheduleId, selectedScheduleGroupId, showProjectDeadlines } = props;
  const calendarLocale = (props.i18n?.language || 'hu').split('-')[0];
  const history = useHistory();

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedScheduleGroup, setSelectedScheduleGroup] = useState(null);
  const [events, setEvents] = useState([]);
  const [dragOverScheduleItemId, setDragOverScheduleItemId] = useState(null);
  const [scheduleItem, setScheduleItem] = useState(null);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [calendarType, setCalendarType] = useState(null);
  const [productionStatus, setProductionStatus] = useState([]);
  const calendarRef = useRef(null);
  const [productionJobDialogVisible, setProductionJobDialogVisible] = useState(false);
  const [productionJobDialogLoading, setProductionJobDialogLoading] = useState(false);
  const [pendingScheduleItemId, setPendingScheduleItemId] = useState(null);
  const [pendingProductionItemId, setPendingProductionItemId] = useState(null);
  const [pendingProductionItem, setPendingProductionItem] = useState(null);
  const [productionJobQuantity, setProductionJobQuantity] = useState(1);
  const [productionJobScheduleFinished, setProductionJobScheduleFinished] = useState(false);
  const [productionJobStandardTime, setProductionJobStandardTime] = useState(null);
  const [productionJobNote, setProductionJobNote] = useState('');
  const [detailsSidebarVisible, setDetailsSidebarVisible] = useState(false);
  const [detailsScheduleId, setDetailsScheduleId] = useState(null);
  const [detailsScheduleGroupType, setDetailsScheduleGroupType] = useState(null);

  const scheduleItemUsedStandardTime = (scheduleItem?.production_jobs || []).reduce((sum, job) => {
    return sum + Number(job?.standard_time || 0);
  }, 0);

  const getScheduleEffectiveCapacityMinutes = (scheduleLike) => {
    if (!scheduleLike?.date_from || !scheduleLike?.date_until) {
      return 0;
    }

    const grossMinutes = moment(scheduleLike.date_until).diff(moment(scheduleLike.date_from), 'minutes');
    const breakMinutes = Number(scheduleLike.break_minutes || 0);

    return Math.max(0, grossMinutes - breakMinutes);
  };

  const scheduleItemShiftCapacityMinutes =
    getScheduleEffectiveCapacityMinutes(scheduleItem?.schedule);

  const scheduleItemFullnessPercent = scheduleItemShiftCapacityMinutes > 0
    ? Math.round((scheduleItemUsedStandardTime / scheduleItemShiftCapacityMinutes) * 100)
    : 0;

  const [selectedScheduleEventProps, setSelectedScheduleEventProps] = useState(null);
  const getCalendarInitialDate = () => {
    const scheduleDate = window.App.urlVars().scheduleDate;
    return scheduleDate && moment(scheduleDate, 'YYYY-MM-DD', true).isValid()
      ? scheduleDate
      : moment().format('YYYY-MM-DD');
  };
  const [calendarInitialDate, setCalendarInitialDate] = useState(
    getCalendarInitialDate()
  );

  const apiCall = (method, url, data) => {
    return axios({
      method,
      url: process.env.REACT_APP_API_HOST + url,
      data,
      params: {},
      headers: {
        'Authorization': 'Bearer ' + localStorage.getItem('userToken'),
        'App-Network': localStorage.getItem('networkId'),
      },
    });
  };

  const apiCallBlob = (method, url) => {
    return axios({
      method,
      url: process.env.REACT_APP_API_HOST + url,
      responseType: 'blob',
      headers: {
        'Authorization': 'Bearer ' + localStorage.getItem('userToken'),
        'App-Network': localStorage.getItem('networkId'),
      },
    });
  };

  const downloadProductionJobWorksheet = (jobId) => {
    if (!jobId) return;

    apiCallBlob('get', `/api/v2/production-job/${jobId}/download/pdf`)
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `production_job_${jobId}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => {
        window.App.toastShow('error', t('error'), error.toString(), '', error.response);
      });
  };

  const getProductionStatusSeverity = (status) => {
    const key = status ? status.toString().toUpperCase() : '';
    switch (key) {
      case 'NEW':
        return 'primary';
      case 'SCHEDULED':
        return 'secondary';
      case 'IN_PROGRESS':
        return 'info';
      case 'PAUSED':
      case 'SUSPENDED':
        return 'warning';
      case 'DONE':
        return 'success';
      case 'DELETED':
        return 'danger';
      default:
        return null;
    }
  };

  const getProductionStatusLabel = (status) => {
    if (!status) return '-';
    const key = status.toString().toUpperCase();
    const item = productionStatus.find((statusItem) => statusItem.key === key);

    return item?.value || key;
  };

  const renderProductionStatusBadge = (status) => {
    return (
      <Badge
        value={getProductionStatusLabel(status)}
        severity={getProductionStatusSeverity(status)}
      />
    );
  };

  const loadScheduleItem = (id) => {
    if (!id) return;
    setLoading(true);
    setDialogVisible(true);
    setDialogLoading(true);
    setScheduleItem(null);
    apiCall('get', `/api/v2/schedule-item/${id}`)
      .then((response) => {
        setScheduleItem(response?.data?.data || null);
      })
      .catch((error) => {
        window.App.toastShow('error', t('error'), error.toString(), '', error.response);
        setScheduleItem(null);
      })
      .finally(() => {
        setDialogLoading(false);
        setLoading(false);
      });
  }

  const mapToEvents = (items) => {
    const out = [];

    const standardTimeBySchedule = {};

    (items || []).forEach((item) => {
      const scheduleId = item?.schedule?.id;
      if (!scheduleId) return;
      const jobs = item?.production_jobs || [];
      const usedStandardTime = jobs.reduce((sum, job) => {
        return sum + Number(job?.standard_time || 0);
      }, 0);
      standardTimeBySchedule[scheduleId] =
        (standardTimeBySchedule[scheduleId] || 0) + usedStandardTime;
    });

    (items || []).forEach((item) => {
      const start = item?.date_from ? moment(item.date_from).format('YYYY-MM-DDTHH:mm:ss') : null;
      const end = item?.date_until ? moment(item.date_until).format('YYYY-MM-DDTHH:mm:ss') : start;
      if (!start) return;

      const resourceParts = [];
      if (item?.machine?.name) resourceParts.push(item.machine.name);
      if (item?.employee?.name) resourceParts.push(item.employee.name);
      const resourceName = resourceParts.join(' - ');

      const jobs = item?.production_jobs || [];
      const jobCount = jobs.length;
      const scheduleId = item?.schedule?.id;
      const usedStandardTime = standardTimeBySchedule[scheduleId] || 0;
      const shiftCapacityMinutes = getScheduleEffectiveCapacityMinutes(item?.schedule);
          
      const fullnessPercent = shiftCapacityMinutes > 0
        ? Math.round((usedStandardTime / shiftCapacityMinutes) * 100)
        : 0;

      const progressPercent = Math.min(fullnessPercent, 100);

      out.push({
        id: `item-${item.id}`,
        title: resourceName,
        start,
        end,
        allDay: false,
        backgroundColor: jobCount > 0 ? '#883cae' : '#e2e8f0',
        borderColor: jobCount > 0 ? '#883cae' : '#cbd5e1',
        textColor: '#000',
        extendedProps: {
          original_item: item,
          resourceName: resourceName,
          jobs: jobs,
          jobCount: jobCount,
          usedStandardTime: usedStandardTime,
          shiftCapacityMinutes: shiftCapacityMinutes,
          fullnessPercent: fullnessPercent,
          progressPercent: progressPercent
        }
      });
    });

    return out;
  };

  const mapProjectsToDeadlineEvents = (projects) => {
    return (projects || [])
      .filter(project => project?.deadline)
      .map(project => {
        const date = moment(project.deadline).format('YYYY-MM-DD');
        return {
          id: `project-deadline-${project.id}`,
          title: project.project_number || project.name || `Projekt #${project.id}`,
          start: date,
          end: date,
          allDay: true,
          backgroundColor: '#f59e0b',
          borderColor: '#d97706',
          textColor: '#000',
          extendedProps: {
            type: 'PROJECT_DEADLINE',
            project
          }
        };
      });
  };

  const loadProjectDeadlinesForRange = async (rangeStart, rangeEnd) => {
    const from = moment(rangeStart).format('YYYY-MM-DD');
    const to = moment(rangeEnd).subtract(1, 'day').format('YYYY-MM-DD');
    const query = [
      'sort=deadline',
      'direction=asc',
      'item_per_page=999',
      'search[deadline][operator]=between',
      `search[deadline][value]=${encodeURIComponent(`${from} 00:00:00|${to} 23:59:59`)}`
    ].join('&');
    const res = await apiCall('get', `/api/v2/project?${query}`);
    return res?.data?.data?.items || [];
  };

  const loadScheduleItemsForRange = async (rangeStart, rangeEnd) => {
    const groupId = selectedScheduleGroupId || null;

    setLoading(true);
    try {
      const from = moment(rangeStart).subtract(1, 'day').format('YYYY-MM-DD HH:mm:ss');
      const to = moment(rangeEnd).endOf('day').format('YYYY-MM-DD HH:mm:ss');

      let url = `/api/v2/schedule-item-calendar?search[from]=${encodeURIComponent(from)}&search[to]=${encodeURIComponent(to)}`;
      if (scheduleId) {
        url += `&search[schedule]=${scheduleId}`;
      } else if (groupId) {
        url += `&search[scheduleGroup]=${groupId}`;
      }
      const res = await apiCall('get', url);
      const items = res?.data?.data?.items || [];
      let mappedEvents = mapToEvents(items);
      if (showProjectDeadlines) {
        const projects = await loadProjectDeadlinesForRange(rangeStart, rangeEnd);
        mappedEvents = [
          ...mappedEvents,
          ...mapProjectsToDeadlineEvents(projects)
        ];
      }
      setEvents(mappedEvents);
    } catch (error) {
      window.App.toastShow('error', 'Hiba történt!', error.toString(), '', error?.response);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const postProductionJobCalendar = async (scheduleItemId, productionItemId, quantity, scheduleFinished, standardTime, note) => {
    setLoading(true);
    try {
      const payload = {
        schedule_item: scheduleItemId,
        production_item: productionItemId,
        quantity: quantity,
        schedule_finished: !!scheduleFinished,
        note: note
      };

      if (pendingProductionItem?.technology?.standard_time) {
        payload.standard_time = standardTime;
      }
      await apiCall('post', '/api/v2/production-job/calendar', payload);
      window.App.toastShow('success', 'Sikeres mentés!');
      setProductionJobDialogVisible(false);
      setPendingScheduleItemId(null);
      setPendingProductionItemId(null);
      setPendingProductionItem(null);
      setProductionJobQuantity(1);
      setProductionJobScheduleFinished(false);
      setProductionJobStandardTime(null);
      setProductionJobNote('');
      const calendarApi = calendarRef.current?.getApi?.();
      if (calendarApi) {
        await loadScheduleItemsForRange(calendarApi.view.activeStart, calendarApi.view.activeEnd);
      }
      window.App.setState({
        rerenderProductionitemCalendarList: Math.random()
      });
    } catch (error) {
      window.App.toastShow('error', t('error'), error.toString(), '', error.response);
    } finally {
      setLoading(false);
      setDragOverScheduleItemId(null);
    }
  };

  const openProductionJobDialog = async (scheduleItemId, productionItemId) => {
    if (!scheduleItemId || !productionItemId) return;
    if (props.onAssign) {
      setDragOverScheduleItemId(null);
      props.onAssign(productionItemId, scheduleItemId);
      return;
    }
    setPendingScheduleItemId(scheduleItemId);
    setPendingProductionItemId(productionItemId);
    setPendingProductionItem(null);
    setProductionJobQuantity(1);
    setProductionJobScheduleFinished(false);
    setProductionJobDialogVisible(true);
    setProductionJobDialogLoading(true);
    try {
      const response = await apiCall('get', `/api/v2/production-item/${productionItemId}`);
      const item = response?.data?.data || null;
      setPendingProductionItem(item);
      setProductionJobQuantity(1);
    } catch (error) {
      window.App.toastShow('error', t('error'), error.toString(), '', error.response);
      setPendingProductionItem(null);
    } finally {
      setProductionJobDialogLoading(false);
    }
  };

  const handleDropToScheduleItem = async (event, scheduleItemId) => {
    event.preventDefault();
    event.stopPropagation();
    const productionItemId = droppedProductionItem(event);
    if (!productionItemId || !scheduleItemId) return;
    await openProductionJobDialog(scheduleItemId, productionItemId);
  };

  const calendarOptions = useMemo(
    () => ({
      plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
      initialView: 'timeGridWeek',
      headerToolbar: {
        left: 'prev,next today',
        center: 'title',
        right: 'timeGridWeek,timeGridDay',
      },
      slotLabelFormat: {
        hour: 'numeric',
        minute: '2-digit',
        omitZeroMinute: false,
        meridiem: false,
      },
      locales: [huLocale, deLocale],
      locale: calendarLocale === 'de' ? 'de' : (calendarLocale === 'en' ? 'en' : 'hu'),
      selectable: true,
      selectMirror: true,
      unselectAuto: true,
      // dayMaxEventRows: 4,
      height: 'auto',
      displayEventTime: false,
      slotEventOverlap: true,
      // allDaySlot: false,
      allDaySlot: true
    }),
    [calendarLocale]
  );

  const formatScheduleItemDateRange = (dateFrom, dateUntil) => {
    if (!dateFrom || !dateUntil) {
      return '-';
    }
    return `${window.App.formatDateValue(dateFrom)} - ${window.App.formatDateValue(dateUntil)}`;
  };

  useEffect(() => {
    const calendarApi = calendarRef.current?.getApi?.();
    if (!calendarApi) {
      return;
    }
    loadScheduleItemsForRange(calendarApi.view.activeStart, calendarApi.view.activeEnd);
  }, [showProjectDeadlines, props.assignmentRefresh]);

  useEffect(() => {
    setCalendarInitialDate(getCalendarInitialDate());
  }, [window.location.hash]);

  useEffect(() => {
    if (scheduleId) return;
    if (!groups?.length) return;
    const selectedGroup = groups.find((group) => group.id === selectedScheduleGroupId);
    if (selectedGroup) {
      const sg = { id: selectedGroup.id, type: selectedGroup.type };
      setSelectedScheduleGroup(sg);
      setCalendarType(selectedGroup.type);
      window.App.setState({ selectedScheduleGroup: sg });
    } else if (selectedScheduleGroupId === null) {
      setSelectedScheduleGroup(null);
      setCalendarType(null);
    }
  }, [selectedScheduleGroupId, groups, scheduleId]);

  useEffect(() => {
    if (scheduleId) {
      setLoading(true);
      apiCall('get', '/api/v2/schedule/' + scheduleId)
        .then((response) => {
          const schedule = response?.data?.data || null;
          const group = schedule?.schedule_group || null;
          if (group?.id) {
            const sg = { id: group.id, type: group.type };
            setSelectedScheduleGroup(sg);
            setCalendarType(group.type);
            window.App.setState({ selectedScheduleGroup: sg });
          }
        })
        .catch((error) => {
          window.App.toastShow('error', t('error'), error.toString(), '', error.response);
        })
        .finally(() => {
          setLoading(false);
        });
      return;
    }

    setLoading(true);
    axios
      .get(process.env.REACT_APP_API_HOST + '/api/v2/schedule-group', {
        params: {},
        headers: {
          'Authorization': 'Bearer ' + localStorage.getItem('userToken'),
          'App-Network': localStorage.getItem('networkId'),
        },
      })
      .then((response) => {
        const items = response.data.data.items ?? [];
        setGroups(items);
      })
      .catch(() => {
      })
      .finally(() => {
        setLoading(false);
      });
  }, [scheduleId, selectedScheduleGroupId]);

  // useEffect(() => {
  //   if (scheduleId) {
  //     return;
  //   }
  //   const start = moment().startOf('month').toDate();
  //   const end = moment().endOf('month').add(1, 'day').toDate();
  //   loadScheduleItemsForRange(start, end);
  // }, [scheduleId, selectedScheduleGroup?.id, selectedScheduleGroupId]);

  useEffect(() => {
    if (scheduleId) {
      return;
    }
    const calendarApi = calendarRef.current?.getApi?.();
    if (!calendarApi) {
      return;
    }
    loadScheduleItemsForRange(calendarApi.view.activeStart, calendarApi.view.activeEnd);
  }, [selectedScheduleGroupId]);

  useEffect(() => {
    apiCall('get', '/api/v2/dictionary/production-status')
      .then((response) => {
        setProductionStatus(response?.data?.data || []);
      })
      .catch((error) => {
        window.App.toastShow('error', t('error'), error.toString(), '', error.response);
      });
  }, []);

  const handleDatesSet = (arg) => {
    loadScheduleItemsForRange(arg.start, arg.end);
  };

  const handleEventClick = (clickInfo) => {
    const type = clickInfo?.event?.extendedProps?.type;
    if (type === 'PROJECT_DEADLINE') {
      const projectId = clickInfo?.event?.extendedProps?.project?.id;

      if (projectId) {
        history.push('/project/project/read/' + projectId);
      }
      return;
    }
    setSelectedScheduleEventProps(clickInfo?.event?.extendedProps || null);
    const scheduleItemId = clickInfo?.event?.extendedProps?.original_item?.id;
    loadScheduleItem(scheduleItemId);
  };

  const renderEventContent = (eventInfo) => {
    const { event } = eventInfo;
    const scheduleItemId = event.extendedProps?.original_item?.id;
    const resourceName = event.extendedProps?.resourceName;
    const isJob = event.extendedProps?.type === 'JOB';
    const jobCount = event.extendedProps?.jobs?.length || 0;
    const isDragOver = dragOverScheduleItemId === scheduleItemId;
    const usedStandardTime = event.extendedProps?.usedStandardTime || 0;
    const shiftCapacityMinutes = event.extendedProps?.shiftCapacityMinutes || 0;
    const fullnessPercent = event.extendedProps?.fullnessPercent || 0;
    const progressPercent = event.extendedProps?.progressPercent || 0;

    const isProjectDeadline = event.extendedProps?.type === 'PROJECT_DEADLINE';

    if (isProjectDeadline) {
      return (
        <div
          style={{
            padding: '3px 5px',
            fontSize: '11px',
            fontWeight: '700',
            cursor: 'pointer',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
          title={t('projectView')}
        >
          <i className="pi pi-calendar-times p-mr-1"></i>
          {event.title}
        </div>
      );
    }

    return (
      <div
        className={'production-calendar-drop-zone ' + (isDragOver ? 'production-calendar-drop-zone-active' : '')}
        style={{ 
          height: '100%', 
          padding: '4px', 
          fontSize: '11px', 
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOverScheduleItemId(scheduleItemId);
        }}
        onDragLeave={(e) => {
          const related = e.relatedTarget;
          if (!e.currentTarget.contains(related)) {
            setDragOverScheduleItemId(null);
          }
        }}
        onDrop={(e) => handleDropToScheduleItem(e, scheduleItemId)}
      >
        <div style={{ 
          fontWeight: 'bold', 
          fontSize: '10px', 
          textTransform: 'uppercase',
          marginBottom: '2px',
          borderBottom: isJob ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.05)'
        }}>
          {resourceName}
        </div>
        
        {isJob && (
          <div style={{ fontStyle: 'italic', flexGrow: 1, marginTop: '2px' }}>
            {event.title}
          </div>
        )}

        {jobCount > 0 && usedStandardTime > 0 && (
          <div style={{ marginTop: 'auto' }}>
            <div
              style={{
                fontSize: '9px',
                fontWeight: '700',
                textAlign: 'right',
                lineHeight: '11px',
                marginBottom: '2px',
                whiteSpace: 'nowrap'
              }}
              title={`${t('standardTime')}: ${usedStandardTime} / ${shiftCapacityMinutes} ${t('minute')} · ${t('tasks')}: ${jobCount} db`}
            >
              {fullnessPercent}%
            </div>
            {usedStandardTime && (

              <div
                style={{
                  width: '100%',
                  height: '5px',
                  background: 'rgba(255,255,255,0.45)',
                  borderRadius: '5px',
                  overflow: 'hidden'
                }}
              >
                <div
                  style={{
                    width: `${progressPercent}%`,
                    height: '100%',
                    background: '#22c55e'
                  }}
                />
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={'scheduleitem-calendar widget'}>
      {loading && <ProgressBar mode="indeterminate" style={{ height: '5px' }} />}

      <div className={'p-grid p-d-none'}>
        <div className={'p-col-12 p-lg-6 p-col-align-center'}></div>

        <div className={'p-col-12 p-lg-6 p-col-align-center p-text-right'}>
          <Dropdown
            options={groups}
            optionLabel="name"
            optionValue="id"
            id={'groups'}
            showClear
            emptyMessage={t('no_dropdown_options')}
            onChange={(e) => {
              let _group = groups.filter((group) => group.id === e.value);
              let _type = '';
              if (_group && _group[0]) _type = _group[0].type;

              const sg = { id: e.value, type: _type };
              setSelectedScheduleGroup(sg);
              setCalendarType(_type);
              window.App.setState({ selectedScheduleGroup: sg });
            }}
            value={selectedScheduleGroup?.id}
          />
        </div>
      </div>

      {/* {calendarType === 'MACHINE_CAPACITY' && ( */}
     
        <FullCalendar
          key={`${scheduleId || 'group'}-${calendarInitialDate}`}
          ref={calendarRef}
          {...calendarOptions}
          events={events}
          initialDate={calendarInitialDate}
          datesSet={handleDatesSet}
          eventClick={handleEventClick}
          eventContent={renderEventContent}
        />
      <Dialog
        header={t('scheduleDetails')}
        visible={dialogVisible}
        style={{ width: '60vw' }}
        contentStyle={{ padding: 0 }}
        onHide={() => {
          setDialogVisible(false);
          setScheduleItem(null);
          setSelectedScheduleEventProps(null);
        }}
      >
        {dialogLoading && <ProgressBar mode="indeterminate" style={{ height: '5px' }} />}

        <div className="p-p-3">
          <div><strong>{t('scheduleGroup')}:</strong> {scheduleItem?.schedule_group?.name || '-'}</div>
          <div>
            <strong>{t('schedule')}:</strong>{' '}
            {formatScheduleItemDateRange(
              scheduleItem?.schedule?.date_from,
              scheduleItem?.schedule?.date_until
            )}
          </div>
          <div>
            <strong>{t('dateTime')}:</strong>{' '}
            {formatScheduleItemDateRange(
              scheduleItem?.date_from,
              scheduleItem?.date_until
            )}
          </div>
          {scheduleItem?.employee?.name && (
            <div><strong>{t('employee')}:</strong> {scheduleItem?.employee?.name || '-'}</div>
          )}
          {scheduleItem?.machine?.name && (
            <div><strong>{t('machine')}:</strong> {scheduleItem?.machine?.name || '-'}</div>
          )}
          {selectedScheduleEventProps && (
            <div className="p-mt-2">
              <div>
                <strong>{t('scheduleUtilization')}:</strong>{' '}
                {selectedScheduleEventProps.fullnessPercent || 0}% (
                {selectedScheduleEventProps.usedStandardTime || 0} {t('standardTimeMinutes')}{' '}
                {selectedScheduleEventProps.shiftCapacityMinutes || 0} {t('fromMinutes')}).
              </div>
{/* 
              <div
                style={{
                  width: '100%',
                  height: '6px',
                  background: '#e5e7eb',
                  borderRadius: '6px',
                  overflow: 'hidden',
                  marginTop: '4px'
                }}
              >
                <div
                  style={{
                    width: `${selectedScheduleEventProps.progressPercent || 0}%`,
                    height: '100%',
                    background: '#22c55e'
                  }}
                />
              </div> */}
            </div>
          )}
          
          <div className="p-mt-3">
            <strong>{t('tasks')}:</strong>
          </div>
          {!dialogLoading && scheduleItem && !scheduleItem.production_jobs?.length && (
            <div className="p-text-secondary p-mt-2">{t('noTask')}</div>
          )}
          {(scheduleItem?.production_jobs || []).map((job, index) => (
            <div key={index} className="p-p-2 p-mt-2" style={{ border: '3px solid #eee', borderRadius: '4px' }}>
              <div><strong>{t('name')}:</strong> {job?.production_item?.name || '-'}</div>
               <div className="p-mt-1"><strong>{t('status')}:</strong>{' '}{renderProductionStatusBadge(job?.status)}</div>
              <div><strong>{t('externalReference')}:</strong> {job?.production_item?.external_reference || '-'}</div>
              <div><strong>{t('projectNumber')}:</strong> {job?.production_item?.project_number || '-'}</div>
              <div>
                <strong>{t('quantity')}:</strong>{' '}
                {job?.quantity ?? 0} {job?.unit?.name || ''}
              </div>
              <div><strong>{t('standardTime')}:</strong> {scheduleItemUsedStandardTime} {t('minute')} ({scheduleItemFullnessPercent} %)</div>
              <div className="p-mt-2">
                <Button
                  type="button"
                  label={t('downloadWorksheet')}
                  icon="pi pi-download"
                  className="p-button-secondary p-button-sm"
                  onClick={() => downloadProductionJobWorksheet(job?.id)}
                  disabled={!job?.id}
                />
              </div>
            </div>
          ))}
          <br></br>
          <Button
            type="button"
            label={t('moreDetails')}
            className="p-button p-button-info p-button-sm"
            disabled={!scheduleItem?.schedule?.id}
            onClick={() => {
              setDetailsScheduleId(scheduleItem.schedule.id);
              setDetailsScheduleGroupType(scheduleItem?.schedule_group?.type || null);
              setDialogVisible(false);
              setDetailsSidebarVisible(true);
            }}
          />
        </div>
      </Dialog>

      
       <Sidebar
        position="left"
        showCloseIcon={false}
        visible={detailsSidebarVisible}
        closeOnEscape={true}
        style={{width: '98%'}}
        onHide={() => {
          setDetailsSidebarVisible(false);
          setDetailsScheduleId(null);
          setDetailsScheduleGroupType(null);
        }}
      >
        <div className={"PageContent p-p-5 p-mx-auto"}>
          <div className={"p-grid"}>
            <div className={"p-col-12 p-lg-6 p-col-align-center p-text-center p-text-lg-left"}>
              <h2 className={"p-m-0 p-p-0 text-primary p-d-inline"}>
                {t('scheduleParticipants')}
              </h2>
            </div>
            <div className={"p-col-12 p-lg-6 p-col-align-center p-text-center p-text-lg-right"}>
              <Button
                type="button"
                label={t('back')}
                icon="pi pi-arrow-left"
                className="p-button-secondary"
                onClick={() => {
                  setDetailsSidebarVisible(false);
                  setDetailsScheduleId(null);
                  setDetailsScheduleGroupType(null);
                }}
              />
            </div>
          </div>
          <div className="p-mt-3">
            <ScheduleitemListComponent scheduleId={detailsScheduleId} scheduleGroupType={detailsScheduleGroupType} />
          </div>
        </div>
      </Sidebar>


      <Dialog
        header={t('assignTask')}
        visible={productionJobDialogVisible}
        style={{ width: '50vw' }}
        onHide={() => {
          setProductionJobDialogVisible(false);
          setPendingScheduleItemId(null);
          setPendingProductionItemId(null);
          setPendingProductionItem(null);
          setProductionJobQuantity(1);
          setProductionJobScheduleFinished(false);
          setDragOverScheduleItemId(null);
        }}
        footer={
          <div>
            <Button
              type="button"
              label={t('cancel')}
              icon="pi pi-times"
              className="p-button"
              onClick={() => {
                setProductionJobDialogVisible(false);
                setPendingScheduleItemId(null);
                setPendingProductionItemId(null);
                setPendingProductionItem(null);
                setProductionJobQuantity(1);
                setProductionJobScheduleFinished(false);
                setDragOverScheduleItemId(null);
                setProductionJobStandardTime(null);
                setProductionJobNote('');
              }}
            />
            <Button
              type="button"
              label={t('assign')}
              icon="pi pi-check"
              className="p-button-success"
              disabled={productionJobDialogLoading || !pendingScheduleItemId || !pendingProductionItemId}
              onClick={() => {
                postProductionJobCalendar(
                  pendingScheduleItemId,
                  pendingProductionItemId,
                  productionJobQuantity,
                  productionJobScheduleFinished,
                  productionJobStandardTime,
                  productionJobNote
                );
              }}
            />
          </div>
        }
      >
        {productionJobDialogLoading && <ProgressBar mode="indeterminate" style={{ height: '5px' }} />}
        <div className="p-p-3">
          <div className="p-grid">
            <div className="p-col-12 p-lg-6">
              <div
                className="p-p-3"
                style={{
                  border: '1px solid var(--surface-d)',
                  borderRadius: 6,
                  height: '100%'
                }}
              >
                <h4 className="p-mt-0 p-mb-3 text-primary">
                  {t('projectItem')}
                </h4>

                <p>
                  <strong>{t('columnName')}:</strong>{' '}
                  {pendingProductionItem?.project_item?.name || '-'}
                </p>

                <p>
                  <strong>{t('externalReference')}:</strong>{' '}
                  {pendingProductionItem?.project_item?.external_reference || '-'}
                </p>

                <p>
                  <strong>{t('itemQuantity')}:</strong>{' '}
                  {pendingProductionItem?.project_item?.quantity || 0}{' '}
                  {pendingProductionItem?.project_item?.unit?.name || ''}
                </p>
              </div>
            </div>

            <div className="p-col-12 p-lg-6">
              <div
                className="p-p-3"
                style={{
                  border: '1px solid var(--surface-d)',
                  borderRadius: 6,
                  height: '100%'
                }}
              >
                <h4 className="p-mt-0 p-mb-3 text-primary">
                  {t('productionItem')}
                </h4>

                <p>
                  <strong>{t('technology')}:</strong>{' '}
                  {pendingProductionItem?.name || '-'}
                </p>

                <p>
                  <strong>{t('projectNumber')}:</strong>{' '}
                  {pendingProductionItem?.project_number || '-'}
                </p>

                <p>
                  <strong>{t('status')}:</strong>{' '}
                  {renderProductionStatusBadge(pendingProductionItem?.status)}
                </p>

                <p>
                  <strong>{t('quantity')}:</strong>{' '}
                  {pendingProductionItem?.quantity || 0}{' '}
                  {pendingProductionItem?.unit?.name || ''}
                </p>

                <p>
                  <strong>{t('assignedQuantity')}:</strong>{' '}
                  {pendingProductionItem?.quantity_in_jobs || 0}{' '}
                  {pendingProductionItem?.unit?.name || ''}
                </p>
              </div>
            </div>
          </div>
          {pendingProductionItem?.technology?.standard_time && (
            <div className="p-grid p-mt-3">
              <div className={"p-col-12 p-lg-6"}>
                <div className="p-field p-fluid">
                  <label htmlFor="productionJobStandardTime">
                    {t('fieldStandardTime')}
                  </label>
                  <div className="p-inputgroup">
                    <InputNumber
                      id="productionJobStandardTime"
                      value={productionJobStandardTime}
                      onValueChange={(e) => setProductionJobStandardTime(e.value)}
                      min={0}
                      useGrouping={false}
                    />
                    <span className="p-inputgroup-addon">
                      {t('minute')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="p-grid p-mt-3">
            <div className={"p-col-12 p-lg-6"}>
              <div className="p-field p-fluid">
                <label htmlFor="productionJobQuantity">{t('allocatedQuantity')}</label>
                <div className="p-inputgroup">
                  <InputNumber
                    id="productionJobQuantity"
                    value={productionJobQuantity}
                    onValueChange={(e) => setProductionJobQuantity(e.value)}
                    min={0}
                    useGrouping={false}
                  />
                  <span className="p-inputgroup-addon">
                    {pendingProductionItem?.unit?.name || ''}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="p-grid p-mt-3">
            <div className={"p-col-12 p-lg-6"}>
              <div className="p-field p-fluid">
                <label htmlFor="note">{t('fieldNote')}</label>
                <div className="p-inputgroup">
                  <InputText
                    id="note"
                    value={productionJobNote}
                    onChange={(e) => setProductionJobNote(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="p-grid p-mt-3">
            <div className="p-col-12 p-lg-4">
              <div className="p-field pfluid">
                <label htmlFor="productionJobScheduleFinished" className="p-d-block">{t('taskAssigned')}</label>
                <InputSwitch
                  id="productionJobScheduleFinished"
                  checked={productionJobScheduleFinished}
                  onChange={(e) => setProductionJobScheduleFinished(e.value)}
                />
              </div>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default withTranslation('common')(ScheduleitemCalendarComponent);
